import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import BlurCircularOutlinedIcon from '@mui/icons-material/BlurCircularOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CellRulesDataGrid from 'components/Pages/OperationsSettings/CellRulesDataGrid';
import CellSelectDropdown from 'components/Pages/OperationsSettings/CellRulesDataGrid/CellRuleOptions/CellSelectDropdown';
import {
  cellRuleRevisionLabel,
  cellRulesFromRevision,
  panelCellRuleMarkerIds,
  revisionsForPanel,
} from 'components/Pages/OperationsSettings/CellRulesDataGrid/cellRuleRevisions';
import { PanelHeaderSummary } from 'components/StudyDashboard/MultiplexPanels/PanelHeaderSummary';
import { PanelSlideSummary } from 'components/StudyDashboard/MultiplexPanels/PanelSlideSummary';
import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { CellType } from 'interfaces/cellType';
import type { CellTypingExecutionUnitId } from 'interfaces/jobs/cellTypingMarkerPositivity';
import { MultiplexThresholdConfig } from 'interfaces/jobs/multiplex/thresholdParams';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { MultiplexPanelCellRules } from 'interfaces/study';
import { compact, difference, every, filter, find, fromPairs, includes, isEmpty, map, some, uniq } from 'lodash';
import React, { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import type { CellTypingRequestedOutputs } from '.';
import { ProbeCountBinsSetup } from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides/sections/InferenceModelsSection/PerProbeCountBinsOverrides';
import { SpotDetectionInputSetup } from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides/sections/InferenceModelsSection/SpotDetectionInputSetup';
import {
  getHScoreBinDraftValidation,
  getHScoreBinMaximumDrafts,
  getHScoreCountBinsFromMaximums,
} from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides/sections/InferenceModelsSection/useHScoreBins';
import {
  getDefaultSpotDetectionSetup,
  MAX_SPOT_DETECTION_DILATION_UM,
} from '../../PostProcessingAndCalculateFeatures/spotDetectionInputSetup';
import { BinaryClassifierThresholdFlowParameters } from '../BinaryClassifierThresholdFlowParameters';
import { PanelPositivityTable } from './PanelPositivityTable';
import { scopePanelConfiguration } from './executionUnits';

export interface PanelConfigurationDraft {
  panelRevision: number | null;
  probeThresholds: Record<string, string>;
  probeThresholdLinks?: Record<string, boolean>;
  hScoreBinMaximumDrafts?: Record<string, string[]>;
  markerThresholds: Record<string, string>;
  spotDetectionSetup: ReturnType<typeof getDefaultSpotDetectionSetup>;
  perProbeSetup: Record<string, ProbeCountBinsSetup | undefined>;
  cellTypeIds: string[];
  cellRules: MinimalCellRule[];
  knnTrainingScope: 'selected' | 'same_panel';
  thresholdFlow?: MultiplexThresholdConfig;
}

export const createPanelConfigurationDraft = (panel: MultiplexPanelView): PanelConfigurationDraft => {
  const channels = compact(map(panel.channels, 'saved'));
  return {
    panelRevision: panel.revision ?? null,
    probeThresholds: fromPairs(
      map(filter(channels, { channelModality: 'rna' }), (channel) => [channel.analysisName, '1'])
    ),
    probeThresholdLinks: fromPairs(
      map(filter(channels, { channelModality: 'rna' }), (channel) => [channel.analysisName, true])
    ),
    markerThresholds: {},
    spotDetectionSetup: getDefaultSpotDetectionSetup(),
    perProbeSetup: {},
    cellTypeIds: [],
    cellRules: [],
    knnTrainingScope: 'selected',
  };
};

export const synchronizePanelProbeThresholds = (
  panel: MultiplexPanelView,
  draft: PanelConfigurationDraft
): PanelConfigurationDraft => ({
  ...draft,
  probeThresholds: {
    ...draft.probeThresholds,
    ...fromPairs(
      compact(
        map(panel.channels, ({ saved: channel }) => {
          if (channel?.channelModality !== 'rna' || !draft.probeThresholdLinks?.[channel.analysisName]) return;
          const maximums = draft.hScoreBinMaximumDrafts?.[channel.analysisName];
          if (maximums) return [channel.analysisName, maximums[0].trim() === '' ? '' : String(Number(maximums[0]) + 1)];
          const bins = draft.perProbeSetup[channel.analysisName]?.countBins ?? draft.spotDetectionSetup.countBins;
          const minimum = bins?.[0]?.minCount;
          return [channel.analysisName, Number.isInteger(minimum) && Number(minimum) >= 1 ? String(minimum) : ''];
        })
      )
    ),
  },
});

const isValidCutoff = (value: string | undefined, maximum = Infinity) =>
  Boolean(value?.trim()) && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= maximum;

const isValidThresholdFlow = (config: MultiplexThresholdConfig | undefined) =>
  !config ||
  (every([config.rangeStart, config.rangeEnd, config.maxThreshold], (value) => isValidCutoff(String(value), 1)) &&
    Number(config.rangeStart) < Number(config.rangeEnd) &&
    isValidCutoff(String(config.precisionDecimal), 10) &&
    Number.isInteger(Number(config.precisionDecimal)));

const ThresholdFlowSettings = <FormValues extends FieldValues>({
  control,
  valid,
  unavailable,
  onValueChange,
}: {
  control: Control<FormValues>;
  valid: boolean;
  unavailable: boolean;
  onValueChange: () => void;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        color={valid ? 'inherit' : 'error'}
        aria-label="Cutoff estimate settings"
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <SettingsOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack
          role="dialog"
          aria-label="Cutoff estimate settings"
          spacing={1}
          sx={{ p: 2, width: 520, maxWidth: '100%' }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">Cutoff estimate settings</Typography>
            <IconButton aria-label="Close cutoff estimate settings" onClick={() => setAnchor(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Used only when this run estimates cutoffs. The table keeps the saved cutoffs.
          </Typography>
          {unavailable ? (
            <Alert severity="info">Estimate defaults are unavailable. This run uses the platform defaults.</Alert>
          ) : (
            <BinaryClassifierThresholdFlowParameters control={control} onValueChange={onValueChange} />
          )}
          {!unavailable && !valid && (
            <Alert severity="error">
              Use an increasing range from 0 to 1, a maximum from 0 to 1, and a whole-number precision from 0 to 10.
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end">
            <Button color="inherit" onClick={() => setAnchor(null)}>
              Close
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

const AddCellClassToRuleGrid = ({
  cellTypeIds,
  disabled,
  onAdd,
}: {
  cellTypeIds: string[];
  disabled: boolean;
  onAdd: (cellTypeIds: string[]) => void;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [selectedCellTypes, setSelectedCellTypes] = useState<CellType[]>([]);
  const addableCellTypes = filter(selectedCellTypes, (cellType) => !includes(cellTypeIds, cellType.id));
  const close = () => {
    setAnchor(null);
    setSelectedCellTypes([]);
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="secondary"
        startIcon={<AddIcon />}
        disabled={disabled}
        aria-label="Add cell classes"
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Add classes
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack role="dialog" aria-label="Add cell classes" spacing={1.5} sx={{ p: 2, width: 320, maxWidth: '85vw' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Add cell classes</Typography>
            <IconButton size="small" aria-label="Close class selection" onClick={close}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <CellSelectDropdown
            selectedCellTypeIds={map(selectedCellTypes, 'id')}
            onSelectCellTypes={setSelectedCellTypes}
            disabledCellTypeIds={cellTypeIds}
            excludeAbstract
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" color="inherit" onClick={close}>
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={disabled || isEmpty(addableCellTypes)}
              onClick={() => {
                if (disabled || isEmpty(addableCellTypes)) return;
                onAdd(map(addableCellTypes, 'id'));
                close();
              }}
            >
              Add classes
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export const isPanelConfigurationValid = (
  panel: MultiplexPanelView,
  selectedSlideIds: string[],
  draft: PanelConfigurationDraft,
  requestedOutputs: CellTypingRequestedOutputs = { positivity: true, cellTypes: true, hScores: true }
) => {
  const channels = compact(map(panel.channels, 'saved'));
  const probes = filter(channels, { channelModality: 'rna' });
  const markerIds = map(
    filter(channels, (channel) => channel.channelModality !== 'dna'),
    'analysisName'
  );
  return (
    panel.saved &&
    Boolean(panel.revision) &&
    draft.panelRevision === panel.revision &&
    channels.length === panel.channels.length &&
    uniq(markerIds).length === markerIds.length &&
    !isEmpty(markerIds) &&
    !isEmpty(selectedSlideIds) &&
    uniq(selectedSlideIds).length === selectedSlideIds.length &&
    every(selectedSlideIds, (slideId) => {
      const slide = find(panel.slides, { slideId });
      return slide?.currentSettingsMatch === true && slide.match !== 'unmatched';
    }) &&
    every(probes, (channel) => {
      const setup = { ...draft.spotDetectionSetup, ...draft.perProbeSetup[channel.analysisName] };
      const dilations = setup.segmentationDilationsUm;
      return (
        dilations?.length === 1 &&
        Number.isInteger(dilations[0]) &&
        dilations[0] >= 0 &&
        dilations[0] <= MAX_SPOT_DETECTION_DILATION_UM
      );
    }) &&
    every(
      filter(channels, { channelModality: 'rna' }),
      (channel) =>
        (!(requestedOutputs.positivity || requestedOutputs.cellTypes) ||
          (isValidCutoff(draft.probeThresholds[channel.analysisName]) &&
            (!draft.probeThresholdLinks?.[channel.analysisName] ||
              (Number.isInteger(Number(draft.probeThresholds[channel.analysisName])) &&
                Number(draft.probeThresholds[channel.analysisName]) >= 1)))) &&
        (!requestedOutputs.hScores ||
          !getHScoreBinDraftValidation(
            draft.hScoreBinMaximumDrafts?.[channel.analysisName] ??
              getHScoreBinMaximumDrafts(
                draft.perProbeSetup[channel.analysisName]?.countBins ?? draft.spotDetectionSetup.countBins ?? []
              )
          ).error)
    ) &&
    every(
      draft.markerThresholds,
      (value, markerId) =>
        includes(map(filter(channels, { channelModality: 'mif' }), 'analysisName'), markerId) &&
        (value === '' || isValidCutoff(value, 1))
    ) &&
    isValidThresholdFlow(draft.thresholdFlow) &&
    (!requestedOutputs.cellTypes ||
      (!isEmpty(draft.cellTypeIds) &&
        every(draft.cellTypeIds, (cellTypeId) =>
          some(
            draft.cellRules,
            (rule) => rule.cellTypeId === cellTypeId && rule.ruleValue !== CellRuleValue.Indifferent
          )
        ) &&
        every(
          draft.cellRules,
          (rule) =>
            includes(markerIds, rule.stainTypeId) &&
            includes(draft.cellTypeIds, rule.cellTypeId) &&
            includes([-1, 0, 1, 2], rule.ruleValue)
        )))
  );
};

export const PanelConfigurationEditor = ({
  panel,
  selectedSlideIds,
  onSelectionChange,
  initialDraft,
  thresholdFlowDefaults,
  requestedOutputs = { positivity: true, cellTypes: true, hScores: true },
  cellRuleRevisions,
  initialCellRuleRevision,
  onCellRuleNameChange,
  onDraftChange,
  runAction,
  executionSettings,
  activeUnits,
  expanded: controlledExpanded,
  onExpandedChange,
}: {
  panel: MultiplexPanelView;
  selectedSlideIds: string[];
  onSelectionChange?: (slideId: string, selected: boolean) => void;
  initialDraft?: PanelConfigurationDraft;
  thresholdFlowDefaults?: MultiplexThresholdConfig;
  onCellRuleNameChange?: (name: string) => void;
  cellRuleRevisions?: MultiplexPanelCellRules[];
  initialCellRuleRevision?: MultiplexPanelCellRules;
  requestedOutputs?: CellTypingRequestedOutputs;
  onDraftChange: (draft: PanelConfigurationDraft, valid: boolean) => void;
  runAction?: ReactNode;
  executionSettings?: ReactNode;
  activeUnits?: CellTypingExecutionUnitId[];
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}) => {
  const headerId = useId();
  const [localExpanded, setLocalExpanded] = useState(true);
  const expanded = controlledExpanded ?? localExpanded;
  const panelRevisions = revisionsForPanel(cellRuleRevisions, panel.panelId);
  const restoredRevision = initialCellRuleRevision ?? panelRevisions[0];
  const markerIds = panelCellRuleMarkerIds(panel);
  const [selectedRevisionKey, setSelectedRevisionKey] = useState(
    () => `${restoredRevision?.revision ?? 1}-${restoredRevision?.savedAt ?? ''}`
  );
  const [cellRuleName, setCellRuleName] = useState(() => restoredRevision?.name?.trim() ?? '');
  useEffect(() => {
    onCellRuleNameChange?.(cellRuleName);
  }, [cellRuleName, onCellRuleNameChange]);
  const [draft, setDraft] = useState(() => {
    const sourceDraft = initialDraft ?? createPanelConfigurationDraft(panel);
    const latest = restoredRevision;
    const reused = !sourceDraft.cellRules.length && latest ? cellRulesFromRevision(latest, markerIds) : undefined;
    const applied = reused && !reused.unsupportedReason ? reused : undefined;
    const configuredDraft = {
      ...sourceDraft,
      ...(applied ? { cellTypeIds: applied.cellTypeIds, cellRules: applied.cellRules } : {}),
      ...(thresholdFlowDefaults && !sourceDraft.thresholdFlow ? { thresholdFlow: thresholdFlowDefaults } : {}),
    };
    return synchronizePanelProbeThresholds(panel, configuredDraft);
  });
  const [revisionReuseError, setRevisionReuseError] = useState<string | null>(() =>
    !initialDraft?.cellRules.length && restoredRevision
      ? cellRulesFromRevision(restoredRevision, markerIds).unsupportedReason
      : null
  );
  const { data: cellTypes, isLoading: isLoadingCellTypes, isError: isCellTypesError } = useCellTypeOptions();
  const { control, getValues } = useForm({
    defaultValues: { configParams: initialDraft?.thresholdFlow ?? thresholdFlowDefaults },
  });
  const { stainTypeOptions } = useStainTypeOptions();
  const configurationPanel = activeUnits
    ? {
        ...panel,
        channels: filter(
          panel.channels,
          (channel) =>
            !channel.saved ||
            channel.saved.channelModality === 'dna' ||
            (channel.saved.channelModality === 'rna'
              ? includes(activeUnits, 'calculate_features')
              : some(activeUnits, (unit) => unit !== 'calculate_features'))
        ),
      }
    : panel;
  const channels = compact(map(configurationPanel.channels, 'saved'));
  const probes = filter(channels, { channelModality: 'rna' });
  const markers = filter(channels, { channelModality: 'mif' });
  const hasActiveMifRules =
    requestedOutputs.cellTypes &&
    some(
      draft.cellRules,
      (rule) => rule.ruleValue !== CellRuleValue.Indifferent && some(markers, { analysisName: rule.stainTypeId })
    );
  const ruleChannels = filter(channels, (channel) => channel.channelModality !== 'dna');
  const displayNames = fromPairs(
    map(ruleChannels, (channel) => [
      channel.analysisName,
      channel.displayName?.trim() ||
        (channel.channelModality !== 'rna'
          ? find(stainTypeOptions, { id: channel.stainTypeId })?.displayName
          : undefined) ||
        (channel.channelModality === 'rna' ? 'Unnamed RNA probe' : 'Unnamed marker'),
    ])
  );
  const panelTitle = panel.name?.trim() || 'Multiplex panel';
  const configurationDraft = scopePanelConfiguration(configurationPanel, draft);
  const updateDraft = (changes: Partial<PanelConfigurationDraft>) => {
    const nextDraft = synchronizePanelProbeThresholds(panel, { ...draft, ...changes });
    setDraft(nextDraft);
    onDraftChange(
      nextDraft,
      (!requestedOutputs.cellTypes ||
        (!isLoadingCellTypes &&
          !isCellTypesError &&
          every(nextDraft.cellTypeIds, (cellTypeId) =>
            some(cellTypes, (cellType) => cellType.id === cellTypeId && !cellType.isAbstract)
          ))) &&
        isPanelConfigurationValid(panel, selectedSlideIds, scopePanelConfiguration(panel, nextDraft), requestedOutputs)
    );
  };
  const sourceDraftHadRules = useRef(Boolean(initialDraft?.cellRules.length));
  const initializedFromRevision = useRef(Boolean(!initialDraft?.cellRules.length && restoredRevision));
  const syncedSavedRevision = useRef(false);
  useEffect(() => {
    if (syncedSavedRevision.current || sourceDraftHadRules.current) return;
    const latest = restoredRevision;
    if (!latest) return;
    if (draft.cellRules.length && !initializedFromRevision.current) {
      syncedSavedRevision.current = true;
      return;
    }
    syncedSavedRevision.current = true;
    const reused = cellRulesFromRevision(latest, markerIds);
    setSelectedRevisionKey(`${latest.revision ?? 1}-${latest.savedAt ?? ''}`);
    setRevisionReuseError(reused.unsupportedReason);
    if (reused.unsupportedReason) return;
    updateDraft({ cellTypeIds: reused.cellTypeIds, cellRules: reused.cellRules });
  }, [restoredRevision, markerIds, draft.cellRules.length, updateDraft]);

  const updateProbeBins = (probeId: string, maximums: string[]) => {
    const valid = !getHScoreBinDraftValidation(maximums).error;
    updateDraft({
      hScoreBinMaximumDrafts: { ...draft.hScoreBinMaximumDrafts, [probeId]: maximums },
      perProbeSetup: valid
        ? {
            ...draft.perProbeSetup,
            [probeId]: { ...draft.perProbeSetup[probeId], countBins: getHScoreCountBinsFromMaximums(maximums) },
          }
        : draft.perProbeSetup,
    });
  };

  const updateProbeCutoff = (probeId: string, value: string) => {
    if (!draft.probeThresholdLinks?.[probeId]) {
      updateDraft({ probeThresholds: { ...draft.probeThresholds, [probeId]: value } });
      return;
    }
    const bins = draft.perProbeSetup[probeId]?.countBins ?? draft.spotDetectionSetup.countBins ?? [];
    const maximums = draft.hScoreBinMaximumDrafts?.[probeId] ?? getHScoreBinMaximumDrafts(bins);
    const boundaryShift = Number(value) - Number(bins[0]?.minCount ?? 1);
    const valid = value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= 1;
    updateProbeBins(
      probeId,
      map(maximums, (maximum, index) =>
        index === 0
          ? value.trim() === ''
            ? ''
            : String(Number(value) - 1)
          : valid && maximum.trim() !== ''
          ? String(Number(maximum) + boundaryShift)
          : maximum
      )
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, nextExpanded) =>
        onExpandedChange ? onExpandedChange(nextExpanded) : setLocalExpanded(nextExpanded)
      }
      disableGutters
    >
      <Stack
        id={headerId}
        aria-controls={`${headerId}-content`}
        role="toolbar"
        aria-label={`${panelTitle} execution controls`}
        direction="row"
        alignItems="center"
        sx={(theme) => ({
          ...getTranslucentSurfaceStyles(theme),
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderBottom: 1,
          borderColor: 'divider',
          pr: 2,
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-label={`${panelTitle} configuration`}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <PanelHeaderSummary
            title={panelTitle}
            markerNames={map(ruleChannels, (channel) => displayNames[channel.analysisName])}
          >
            <Chip size="small" label={`${selectedSlideIds.length} selected slides`} />
          </PanelHeaderSummary>
        </AccordionSummary>
        {runAction && <Box sx={{ flexShrink: 0, ml: 2 }}>{runAction}</Box>}
      </Stack>
      <AccordionDetails role="region" aria-label={`${panelTitle} settings`}>
        <Stack spacing={2}>
          <PanelSlideSummary panel={panel} selectedSlideIds={selectedSlideIds} onSelectionChange={onSelectionChange} />
          <Divider />
          {executionSettings}
          {executionSettings && <Divider />}
          {channels.length !== configurationPanel.channels.length && (
            <Alert severity="info">Define every panel marker before configuring cell typing.</Alert>
          )}
          {draft.panelRevision !== panel.revision && (
            <Alert severity="warning">This configuration belongs to an earlier panel revision.</Alert>
          )}
          {isCellTypesError && <Alert severity="error">Cell types could not be loaded.</Alert>}
          {!isEmpty(markers) && (
            <>
              {hasActiveMifRules && (
                <TextField
                  select
                  size="small"
                  label="KNN training scope"
                  sx={{ maxWidth: 360 }}
                  value={draft.knnTrainingScope}
                  onChange={(event) =>
                    updateDraft({ knnTrainingScope: event.target.value as PanelConfigurationDraft['knnTrainingScope'] })
                  }
                >
                  <MenuItem value="selected">Selected slides</MenuItem>
                  <MenuItem value="same_panel">All matching slides in this study panel</MenuItem>
                </TextField>
              )}
            </>
          )}
          {!isEmpty(ruleChannels) && (
            <PanelPositivityTable
              hScoresEnabled={requestedOutputs.hScores}
              channels={ruleChannels}
              displayNames={displayNames}
              draft={configurationDraft}
              headingAction={
                !isEmpty(markers) && (!activeUnits || includes(activeUnits, 'calculate_thresholds')) ? (
                  <ThresholdFlowSettings
                    control={control}
                    valid={isValidThresholdFlow(draft.thresholdFlow)}
                    unavailable={!draft.thresholdFlow}
                    onValueChange={() => updateDraft({ thresholdFlow: getValues('configParams') })}
                  />
                ) : undefined
              }
              onCutoffChange={(markerId, value) => {
                if (some(markers, { analysisName: markerId })) {
                  updateDraft({ markerThresholds: { ...draft.markerThresholds, [markerId]: value } });
                } else updateProbeCutoff(markerId, value);
              }}
              onLinkChange={(probeId, linked) =>
                updateDraft({ probeThresholdLinks: { ...draft.probeThresholdLinks, [probeId]: linked } })
              }
              onBinsChange={updateProbeBins}
              onInheritBins={(probeId) => {
                const maximums = { ...draft.hScoreBinMaximumDrafts };
                delete maximums[probeId];
                updateDraft({
                  perProbeSetup: { ...draft.perProbeSetup, [probeId]: undefined },
                  hScoreBinMaximumDrafts: maximums,
                });
              }}
            />
          )}
          {!isEmpty(probes) && (
            <>
              <Divider />
              <Stack component="section" aria-label="Spot count geometry" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <BlurCircularOutlinedIcon color="action" />
                  <Typography variant="h3" component="h3">
                    Spot count geometry
                  </Typography>
                </Stack>
                <SpotDetectionInputSetup
                  singleDilation
                  showSummary={false}
                  availableGeometries={draft.spotDetectionSetup.segmentationCompartments}
                  showHScoreBins={false}
                  showPositivityThreshold={false}
                  setup={draft.spotDetectionSetup}
                  onChange={(spotDetectionSetup) => updateDraft({ spotDetectionSetup })}
                />
              </Stack>
              <Divider />
            </>
          )}
          {requestedOutputs.cellTypes && !isEmpty(ruleChannels) && (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountTreeOutlinedIcon color="action" />
                <Typography variant="h3" component="h3">
                  Cell rules
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  label="Cell rules name"
                  value={cellRuleName}
                  onChange={(event) => setCellRuleName(event.target.value)}
                  placeholder="Name these rules"
                  helperText="Saving uses an existing revision when these rules already match."
                  sx={{ width: 280, maxWidth: '100%' }}
                />
                {panelRevisions.length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Saved cell rules"
                    value={selectedRevisionKey}
                    onChange={(event) => {
                      const nextKey = event.target.value;
                      const revision = panelRevisions.find(
                        (entry) => `${entry.revision ?? 1}-${entry.savedAt}` === nextKey
                      );
                      if (!revision) return;
                      const reused = cellRulesFromRevision(revision, markerIds);
                      setSelectedRevisionKey(nextKey);
                      setCellRuleName(revision.name?.trim() ?? '');
                      setRevisionReuseError(reused.unsupportedReason);
                      if (reused.unsupportedReason) return;
                      updateDraft({ cellTypeIds: reused.cellTypeIds, cellRules: reused.cellRules });
                    }}
                    error={Boolean(revisionReuseError)}
                    helperText={revisionReuseError || 'Markers from another panel are not available here.'}
                    sx={{ width: 280, maxWidth: '100%' }}
                  >
                    {panelRevisions.map((revision) => (
                      <MenuItem
                        key={`${revision.revision ?? 1}-${revision.savedAt}`}
                        value={`${revision.revision ?? 1}-${revision.savedAt}`}
                      >
                        {cellRuleRevisionLabel(revision)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
              <CellRulesDataGrid
                panelId={panel.panelId}
                stainTypeIds={map(ruleChannels, 'analysisName')}
                markerDisplayNames={displayNames}
                cellTypeHeaderAction={
                  <AddCellClassToRuleGrid
                    cellTypeIds={configurationDraft.cellTypeIds}
                    disabled={!requestedOutputs.cellTypes || isLoadingCellTypes || isCellTypesError}
                    onAdd={(newCellTypeIds) =>
                      updateDraft({
                        cellTypeIds: [...draft.cellTypeIds, ...difference(uniq(newCellTypeIds), draft.cellTypeIds)],
                      })
                    }
                  />
                }
                cellTypeIds={configurationDraft.cellTypeIds}
                rules={configurationDraft.cellRules}
                isLoading={Boolean(isLoadingCellTypes)}
                editable={requestedOutputs.cellTypes}
                adminTableLayout
                onCellValueChange={(cellTypeId, stainTypeId, ruleValue) =>
                  updateDraft({
                    cellRules: [
                      ...filter(
                        draft.cellRules,
                        (rule) => rule.cellTypeId !== cellTypeId || rule.stainTypeId !== stainTypeId
                      ),
                      { cellTypeId, stainTypeId, ruleValue },
                    ],
                  })
                }
              />
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
